import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Shield, Building2, Users, Activity, Mail, Megaphone, Ban, RefreshCw } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";

function Metric({ icon: Icon, label, value }) {
  return (
    <div className="card2 p-5">
      <Icon size={18} className="text-indigo2 mb-2" />
      <div className="t-caption">{label}</div>
      <div className="t-h2 mt-1">{typeof value === "number" ? value.toLocaleString() : value}</div>
    </div>
  );
}

export default function SuperAdmin() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("metrics");
  const [metrics, setMetrics] = useState(null);
  const [firms, setFirms] = useState([]);
  const [emails, setEmails] = useState([]);
  const [apiUsage, setApiUsage] = useState([]);
  const [announce, setAnnounce] = useState({ title: "", body: "", severity: "info" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const m = await api.get("/api/admin/metrics"); setMetrics(m.data);
        const f = await api.get("/api/admin/firms"); setFirms(f.data.items);
      } catch {}
    })();
  }, []);
  useEffect(() => {
    if (tab === "email-log") api.get("/api/admin/email-log").then((r) => setEmails(r.data.items)).catch(() => {});
    if (tab === "api-usage") api.get("/api/admin/api-usage").then((r) => setApiUsage(r.data.items)).catch(() => {});
  }, [tab]);

  if (user === null) return <div className="min-h-screen flex items-center justify-center bg-navy-900"><Loader2 size={32} className="animate-spin text-white" /></div>;
  if (!user || user.role !== "super_admin") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-navy-900 text-white text-center p-6">
        <div>
          <Shield size={48} className="mx-auto mb-4 text-rose2" />
          <h1 className="t-h1 mb-2">Super admin only</h1>
          <p className="t-body text-white/70 mb-6">This area is reserved for Compliche staff.</p>
          <Link to="/app/dashboard" className="btn btn-primary">Back to dashboard</Link>
        </div>
      </div>
    );
  }

  const suspend = async (id) => { if (!window.confirm("Suspend this firm? All sessions will be killed.")) return; setBusy(true); try { await api.post(`/api/admin/firms/${id}/suspend`); const f = await api.get("/api/admin/firms"); setFirms(f.data.items); } catch (e) { alert(formatApiError(e)); } finally { setBusy(false); } };
  const reactivate = async (id) => { setBusy(true); try { await api.post(`/api/admin/firms/${id}/reactivate`); const f = await api.get("/api/admin/firms"); setFirms(f.data.items); } catch (e) { alert(formatApiError(e)); } finally { setBusy(false); } };
  const sendAnnounce = async () => { setBusy(true); try { await api.post("/api/admin/announcement", announce); setAnnounce({ title: "", body: "", severity: "info" }); alert("Sent."); } catch (e) { alert(formatApiError(e)); } finally { setBusy(false); } };

  const TABS = [
    { id: "metrics", label: "Metrics", icon: Activity },
    { id: "firms", label: "Firms", icon: Building2 },
    { id: "email-log", label: "Email log", icon: Mail },
    { id: "api-usage", label: "API usage", icon: Users },
    { id: "announce", label: "Announcement", icon: Megaphone },
  ];

  return (
    <div className="min-h-screen bg-surface flex flex-col">
      <header className="bg-navy-900 text-white">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield size={20} className="text-rose2" />
            <div className="t-h4">Compliche · Super Admin</div>
          </div>
          <div className="flex items-center gap-3">
            <span className="t-caption text-white/60">{user.email}</span>
            <button onClick={logout} className="btn btn-secondary-on-dark btn-sm">Sign out</button>
          </div>
        </div>
      </header>
      <div className="max-w-7xl mx-auto w-full px-6 py-8 grid lg:grid-cols-[200px_1fr] gap-6 flex-1">
        <aside>
          <nav className="card2 p-2 lg:sticky lg:top-20">
            {TABS.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)} className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-md t-body-sm font-medium ${tab === t.id ? "bg-indigo2 text-white" : "text-slate2 hover:bg-surface hover:text-ink"}`} data-testid={`admin-tab-${t.id}`}>
                <t.icon size={16} />{t.label}
              </button>
            ))}
          </nav>
        </aside>
        <main className="min-w-0">
          {tab === "metrics" && metrics && (
            <div className="space-y-4">
              <h2 className="t-h2">Platform metrics</h2>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                <Metric icon={Building2} label="Total firms" value={metrics.total_firms} />
                <Metric icon={Activity} label="Active firms (30d)" value={metrics.active_firms_30d} />
                <Metric icon={Users} label="Users" value={metrics.total_users} />
                <Metric icon={Users} label="Clients onboarded" value={metrics.total_clients} />
                <Metric icon={Activity} label="Compliance scans" value={metrics.total_scans} />
                <Metric icon={Activity} label="API calls" value={metrics.total_api_calls} />
              </div>
              <p className="t-caption">As of {new Date(metrics.as_of).toLocaleString()}</p>
            </div>
          )}
          {tab === "firms" && (
            <div>
              <h2 className="t-h2 mb-4">All firms ({firms.length})</h2>
              <div className="card2 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-surface border-b border-border2 text-left">
                    <tr><th className="px-4 py-3 t-label text-slate2">Name</th><th className="px-4 py-3 t-label text-slate2">Plan</th><th className="px-4 py-3 t-label text-slate2">Status</th><th className="px-4 py-3 t-label text-slate2">Created</th><th className="px-4 py-3 text-right"></th></tr>
                  </thead>
                  <tbody className="divide-y divide-border2">
                    {firms.map((f) => (
                      <tr key={f.id} data-testid={`admin-firm-${f.id}`}>
                        <td className="px-4 py-3 t-body-sm font-medium">{f.name || "—"}</td>
                        <td className="px-4 py-3 t-caption">{f.plan || "trial"}</td>
                        <td className="px-4 py-3">{f.suspended_at ? <span className="badge2 badge-failed">Suspended</span> : f.deletion_scheduled_at ? <span className="badge2 badge-pending">Pending deletion</span> : <span className="badge2 badge-verified">Active</span>}</td>
                        <td className="px-4 py-3 t-caption">{f.created_at ? new Date(f.created_at).toLocaleDateString() : "—"}</td>
                        <td className="px-4 py-3 text-right">
                          {f.suspended_at
                            ? <button onClick={() => reactivate(f.id)} disabled={busy} className="btn btn-ghost btn-sm"><RefreshCw size={14} />Reactivate</button>
                            : <button onClick={() => suspend(f.id)} disabled={busy} className="btn btn-ghost btn-sm text-rose2"><Ban size={14} />Suspend</button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {tab === "email-log" && (
            <div>
              <h2 className="t-h2 mb-4">Email log</h2>
              <div className="card2 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-surface border-b border-border2 text-left"><tr><th className="px-4 py-3 t-label text-slate2">When</th><th className="px-4 py-3 t-label text-slate2">To</th><th className="px-4 py-3 t-label text-slate2">Subject</th><th className="px-4 py-3 t-label text-slate2">Status</th></tr></thead>
                  <tbody className="divide-y divide-border2">
                    {emails.map((e) => (
                      <tr key={e.id || e.at}>
                        <td className="px-4 py-3 t-caption">{e.at ? new Date(e.at).toLocaleString() : "—"}</td>
                        <td className="px-4 py-3 t-body-sm">{e.to}</td>
                        <td className="px-4 py-3 t-body-sm truncate max-w-md">{e.subject}</td>
                        <td className="px-4 py-3"><span className={`badge2 ${e.status === "sent" ? "badge-verified" : e.status === "failed" ? "badge-failed" : "badge-locked"}`}>{e.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {tab === "api-usage" && (
            <div>
              <h2 className="t-h2 mb-4">API usage (last 30 days)</h2>
              <div className="card2 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-surface border-b border-border2 text-left"><tr><th className="px-4 py-3 t-label text-slate2">Firm</th><th className="px-4 py-3 t-label text-slate2">Endpoint</th><th className="px-4 py-3 t-label text-slate2">Calls</th><th className="px-4 py-3 t-label text-slate2">Avg ms</th></tr></thead>
                  <tbody className="divide-y divide-border2">
                    {apiUsage.map((u, i) => (
                      <tr key={i}><td className="px-4 py-3 t-caption t-mono">{u._id.firm_id?.slice(0, 8)}…</td><td className="px-4 py-3 t-body-sm t-mono">{u._id.endpoint}</td><td className="px-4 py-3 t-body-sm">{u.count}</td><td className="px-4 py-3 t-caption">{Math.round(u.avg_ms || 0)}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {tab === "announce" && (
            <div className="max-w-2xl">
              <h2 className="t-h2 mb-4">Send platform-wide announcement</h2>
              <div className="card2 p-6 space-y-4">
                <div><label className="label2">Title</label><input className="input2" value={announce.title} onChange={(e) => setAnnounce({ ...announce, title: e.target.value })} /></div>
                <div><label className="label2">Body</label><textarea className="textarea2" rows={4} value={announce.body} onChange={(e) => setAnnounce({ ...announce, body: e.target.value })} /></div>
                <div><label className="label2">Severity</label><select className="input2" value={announce.severity} onChange={(e) => setAnnounce({ ...announce, severity: e.target.value })}><option value="info">Info</option><option value="warning">Warning</option><option value="critical">Critical</option></select></div>
                <button onClick={sendAnnounce} disabled={busy || !announce.title || !announce.body} className="btn btn-primary">{busy ? <Loader2 size={14} className="animate-spin" /> : <Megaphone size={14} />}Send to all firms</button>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
